import { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './HeroSection.css';

const SLIDE_MEDIA = [
  { img: '/image.webp', pos: 'center 28%', anim: 'wipe' },
  { img: '/femme-entrepreneuses.webp', pos: 'center 22%', anim: 'rise' },
  { img: '/IMG_1410.webp', pos: 'center 20%', anim: 'blur' },
  { img: '/IMG_1412.webp', pos: 'center 26%', anim: 'slide' },
];

export default function HeroSection() {
  const { t } = useTranslation('home');
  const [slide, setSlide]       = useState(0);
  const [phraseKey, setPhraseKey] = useState(0);
  // Seule l'image de la slide 0 est nécessaire au premier rendu (candidate LCP).
  // Les 3 autres (jusqu'à ~640 Ko chacune) ne sont chargées qu'une fois le
  // thread principal libéré, pour ne pas concurrencer le CSS/JS critique.
  const [bgLoaded, setBgLoaded] = useState([true, false, false, false]);

  const slideText = t('hero.slides', { returnObjects: true });
  const SLIDES = SLIDE_MEDIA.map((m, i) => ({ ...m, ...slideText[i] }));

  const current = SLIDES[slide];

  /* ── Chargement différé des images de fond restantes ── */
  useEffect(() => {
    const load = () => setBgLoaded([true, true, true, true]);
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(load, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(load, 2000);
    return () => clearTimeout(id);
  }, []);

  /* ── Slideshow 6 s ── */
  useEffect(() => {
    const id = setInterval(() => {
      setSlide(s => (s + 1) % SLIDES.length);
      setPhraseKey(k => k + 1);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <header className="hs-hero">

      {/* ══ FOND : diaporama ══ */}
      <div className="hs-slides-bg" aria-hidden="true">
        {SLIDES.map((s, i) => (
          <div key={i} className={`hs-slide ${i === slide ? 'is-active' : ''}`}>
            {i === 0 ? (
              // Slide 0 = candidat LCP : vraie balise <img> priorisée (une
              // image de fond CSS ne peut pas recevoir fetchpriority et est
              // toujours traitée en basse priorité par le navigateur).
              <img
                src={s.img}
                alt=""
                fetchpriority="high"
                loading="eager"
                decoding="async"
                className="hs-slide-img"
                style={{ objectPosition: s.pos }}
              />
            ) : (
              <div
                className="hs-slide-img"
                style={bgLoaded[i] ? { backgroundImage: `url(${s.img})`, backgroundPosition: s.pos } : { backgroundPosition: s.pos }}
              />
            )}
          </div>
        ))}
        <div className="hs-overlay" />
      </div>

      <div className="hs-grain" aria-hidden="true" />

      {/* ══ CONTENU GAUCHE ══ */}
      <div className="hs-inner">
        <div className="hs-content">

          {/* Titre de page — masqué visuellement, requis pour le SEO/A11y (une seule h1 par page) */}
          <h1 className="sr-only">{t('hero.tagline')} — {t('hero.desc')}</h1>

          {/* Eyebrow statique */}
          <div className="hs-eyebrow">{t('hero.tagline')}</div>

          {/* Phrase inspirante dynamique — change à chaque slide */}
          <div
            className={`hs-phrase hs-anim-${current.anim}`}
            key={phraseKey}
            aria-live="polite"
          >
            <span className="hs-phrase-tag">{current.tag}</span>
            <p className="hs-phrase-body">
              <span className="hs-pl hs-pl-1">{current.line1}</span>
              <span className="hs-pl hs-pl-2">{current.line2}</span>
            </p>
          </div>

          {/* Sous-titre statique */}
          <p className="hs-sub">{t('hero.desc')}</p>

          {/* CTAs */}
          <div className="hs-actions">
            <Link to="/candidature" className="hs-btn-primary">
              {t('hero.cta_apply')}
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
            <Link to="/a-propos#projet" className="hs-btn-ghost">
              {t('hero.cta_discover')}
            </Link>
          </div>

          {/* Stats */}
          <div className="hs-stats">
            {[
              { num: '1 000', lbl: t('stats.s1_label')  },
              { num: '2',     lbl: t('stats.s3_label')   },
              { num: t('hero.impact_num'), lbl: t('hero.impact_label') },
              { num: '100%',  lbl: t('benefits.b4')      },
            ].map(({ num, lbl }, i) => (
              <Fragment key={num}>
                {i > 0 && <span className="hs-sep" aria-hidden="true">·</span>}
                <div className="hs-stat">
                  <span className="hs-stat-num">{num}</span>
                  <span className="hs-stat-lbl">{lbl}</span>
                </div>
              </Fragment>
            ))}
          </div>

          {/* Badge opérateur FESTIC */}
          <a
            href="https://www.festicsn.com/fr"
            target="_blank"
            rel="noopener noreferrer"
            className="hs-operator"
          >
            <span className="hs-operator-label">{t('hero.operator_label')}</span>
            <span className="hs-operator-name">FESTIC</span>
          </a>
        </div>
      </div>

      {/* ══ DOTS ══ */}
      <nav className="hs-dots" aria-label={t('hero.slideshow_aria')}>
        {SLIDES.map((_, i) => (
          <button
            key={i}
            className={`hs-dot ${i === slide ? 'is-active' : ''}`}
            onClick={() => { setSlide(i); setPhraseKey(k => k + 1); }}
            aria-label={t('hero.slide_aria', { n: i + 1 })}
          />
        ))}
      </nav>

      {/* ══ SCROLL ══ */}
      <div className="hs-scroll" aria-hidden="true">
        <span>{t('hero.scroll')}</span>
        <div className="hs-scroll-line" />
      </div>

    </header>
  );
}
